import apiClient from "../../utils/apiClient";

/**
 * Every portfolio call, in one place.
 *
 * Written as a module rather than inline in the components because the same
 * endpoints are hit from six sections plus the staff view, and a path typed
 * seven times is a path that will be typed wrong once. Each function returns the
 * response body, so callers never handle an axios envelope.
 *
 * Errors are thrown, not swallowed. `errorText` below is the one place that
 * decides what a caller shows, so a failed save never renders "[object Object]".
 */

/** The message an API error should show a user, from wherever the server put it. */
export const errorText = (err, fallback = "Something went wrong. Please try again.") =>
  err?.response?.data?.message || err?.message || fallback;

/** The machine-readable failure code, when the server sent one. */
export const errorCode = (err) => err?.response?.data?.code || "";

/* ── The student's own portfolio ─────────────────────────────────────────── */

export const fetchMyPortfolio = () =>
  apiClient.get("/portfolio/me").then((r) => r.data);

export const saveCareerProfile = (payload) =>
  apiClient.put("/portfolio/me/career-profile", payload).then((r) => r.data);

export const fetchMyAnalyses = () =>
  apiClient.get("/portfolio/me/analyses").then((r) => r.data);

export const saveResume = (payload) =>
  apiClient.put("/portfolio/me/resume", payload).then((r) => r.data);

export const removeResume = () =>
  apiClient.delete("/portfolio/me/resume").then((r) => r.data);

/**
 * Run the AI analysis.
 *
 * ⚠️ The long timeout is the point. A reasoning model reading a two-page resume
 * and returning a dozen structured critiques takes 20-60 seconds, and axios'
 * default would abort a request the server is still working on — the student
 * sees a network error while the institute is billed for the tokens.
 */
export const analyzeResume = (payload = {}) =>
  apiClient.post("/portfolio/me/resume/analyze", payload, { timeout: 180000 }).then((r) => r.data);

/* ── Sub-resources ───────────────────────────────────────────────────────── */
// One shape for all four: add / update / remove, same argument order.

const collection = (path) => ({
  add: (body) => apiClient.post(`/portfolio/me/${path}`, body).then((r) => r.data),
  update: (id, body) => apiClient.put(`/portfolio/me/${path}/${id}`, body).then((r) => r.data),
  remove: (id) => apiClient.delete(`/portfolio/me/${path}/${id}`).then((r) => r.data),
});

export const projectsApi = collection("projects");
export const achievementsApi = collection("achievements");
export const certificationsApi = collection("certifications");
export const videosApi = collection("videos");

/* ── Files ───────────────────────────────────────────────────────────────── */

/**
 * Upload through the existing /files/upload endpoint, which returns the storage
 * key. Nothing in the portfolio uploads directly to R2: the key the server
 * returns is the only one the portfolio API will accept, which is what makes
 * `assertOwnKey` on the backend a real boundary rather than a formality.
 */
export const uploadFile = (file) => {
  const form = new FormData();
  form.append("file", file);
  return apiClient
    .post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};

/* ── Staff view ──────────────────────────────────────────────────────────── */

export const fetchStudentPortfolio = (studentId) =>
  apiClient.get(`/portfolio/student/${studentId}`).then((r) => r.data);

export const fetchStudentAnalyses = (studentId) =>
  apiClient.get(`/portfolio/student/${studentId}/analyses`).then((r) => r.data);

export const fetchPortfolioList = (params = {}) =>
  apiClient.get("/portfolio/students", { params }).then((r) => r.data);
